"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.setPrintingModeEnabled = setPrintingModeEnabled;
exports.setMapTilerApiKey = setMapTilerApiKey;
exports.getMapTilerApiKey = getMapTilerApiKey;
exports.areVectorMapsEnabled = areVectorMapsEnabled;
exports.useVectorMap = useVectorMap;
exports.useHoverCursor = useHoverCursor;
exports.useStyleMap = useStyleMap;
exports.useBaseStyle = useBaseStyle;
exports.mergeBaseAndUserStyle = mergeBaseAndUserStyle;
exports.AttributionControl = AttributionControl;
exports.VectorMapLogo = VectorMapLogo;
exports.usePersistedMapBounds = usePersistedMapBounds;
const maplibre_gl_1 = __importDefault(require("maplibre-gl"));
const react_1 = require("react");
const mapSymbols_1 = require("./mapSymbols");
require("maplibre-gl/dist/maplibre-gl.css");
require("./VectorMapViewComponent.css");
const react_2 = __importDefault(require("react"));
/** Set to true to enable printing by preserving the drawing buffer */
let printingModeEnabled = false;
function setPrintingModeEnabled(val) {
    printingModeEnabled = val;
}
/* Hooks and functions related to displaying a vector map */
let mapTilerApiKey = "";
/** This must be called to set the appropriate key before use. If it is not set, vector maps will not function.
 * Maps will fall back to leaflet if the key is not set or if set to ""
 */
function setMapTilerApiKey(key) {
    mapTilerApiKey = key;
}
function getMapTilerApiKey() {
    return mapTilerApiKey;
}
/** Check if vector maps are enabled by setting API key */
function areVectorMapsEnabled() {
    return mapTilerApiKey !== "";
}
/** Loads a vector map, refreshing the WebGL context as needed */
function useVectorMap(options) {
    const { divRef, bounds, scrollZoom, dragPan, touchZoomRotate } = options;
    // Maplibre map
    const [map, setMap] = (0, react_1.useState)();
    // Tracks if map div is visible
    const [mapDivVisible, setMapDivVisible] = (0, react_1.useState)(false);
    // Tracks if map has webgl context
    const [hasWebGLContext, setHasWebGLContext] = (0, react_1.useState)(false);
    // Increments to trigger a map reload
    const [mapReloadCount, setMapReloadCount] = (0, react_1.useState)(0);
    // Observe visibility of map
    (0, react_1.useEffect)(() => {
        if (!divRef) {
            return;
        }
        const observer = new IntersectionObserver(function (entries) {
            // When in printing mode, always visible as we need to render the map
            setMapDivVisible(entries[0].isIntersecting || printingModeEnabled);
        });
        observer.observe(divRef);
        return () => {
            observer.disconnect();
        };
    }, [divRef]);
    // If map div is visible, and no webgl context, trigger map load
    (0, react_1.useEffect)(() => {
        if (mapDivVisible && !hasWebGLContext) {
            setMapReloadCount(rc => rc + 1);
        }
    }, [mapDivVisible, hasWebGLContext]);
    // Load map and symbols
    (0, react_1.useEffect)(() => {
        // Don't enable if invisible
        if (!mapDivVisible || !divRef) {
            return;
        }
        try {
            const m = new maplibre_gl_1.default.Map({
                container: divRef,
                bounds: bounds,
                scrollZoom: scrollZoom === false ? false : true,
                dragPan: dragPan === false ? false : true,
                touchZoomRotate: touchZoomRotate === false ? false : true,
                attributionControl: false,
                boxZoom: false,
                style: {
                    version: 8,
                    layers: [],
                    sources: {}
                },
                // Prevent scrolling outside of world bounds
                maxBounds: [
                    [-179.9, -85], // Southwest coordinates
                    [179.9, 85] // Northeast coordinates
                ],
                preserveDrawingBuffer: printingModeEnabled
            });
            setHasWebGLContext(true);
            // Add listener for losing context
            m.on("webglcontextlost", () => {
                console.warn("Lost WebGL context");
                setHasWebGLContext(false);
            });
            // Add zoom controls to the map.
            m.addControl(new maplibre_gl_1.default.NavigationControl({}), "top-left");
            // Add scale control
            const scale = new maplibre_gl_1.default.ScaleControl({
                unit: "metric"
            });
            m.addControl(scale);
            // Speed up wheel scrolling
            m.scrollZoom.setWheelZoomRate(1 / 250);
            // Dynamically load symbols
            m.on("styleimagemissing", function (ev) {
                // Check if known
                const mapSymbol = (0, mapSymbols_1.getMapSymbols)().find((s) => s.value == ev.id);
                if (mapSymbol) {
                    m.loadImage(mapSymbol.url, (err, image) => {
                        if (image && !m.hasImage(mapSymbol.value)) {
                            m.addImage(mapSymbol.value, image, { sdf: true });
                        }
                    });
                }
            });
            setMap(m);
            return () => {
                m.remove();
                setHasWebGLContext(false);
            };
        }
        catch (e) {
            console.error("Error loading map", e);
            alert(T `Error loading map. Please check that your browser and drivers are updated.`);
        }
    }, [mapReloadCount]);
    return map;
}
/** Sets cursor as pointer when over any layers with the specified ids */
function useHoverCursor(map, layerIds) {
    // Setup hover effect
    (0, react_1.useEffect)(() => {
        if (!map) {
            return;
        }
        const removes = [];
        for (const layerId of layerIds) {
            const onEnter = (ev) => {
                map.getCanvas().style.cursor = 'pointer';
            };
            const onLeave = (ev) => {
                map.getCanvas().style.cursor = '';
            };
            map.on("mousemove", layerId, onEnter);
            map.on("mouseleave", layerId, onLeave);
            removes.push(() => {
                map.off("mousemove", layerId, onEnter);
                map.off("mouseleave", layerId, onLeave);
            });
        }
        return () => {
            for (const remove of removes) {
                remove();
            }
        };
    }, [map, layerIds]);
}
/** Apply user style to a map with base style */
function useStyleMap(options) {
    const { map, baseLayer, baseLayerOpacity, userStyle } = options;
    // Load base style
    const baseStyle = useBaseStyle(baseLayer);
    // Update map style
    const style = (0, react_1.useMemo)(() => {
        return mergeBaseAndUserStyle(baseStyle, userStyle, baseLayerOpacity);
    }, [baseStyle, userStyle, baseLayerOpacity]);
    // Set map style
    (0, react_1.useEffect)(() => {
        // Can't load until map and style are present
        if (!map || !style) {
            return;
        }
        map.setStyle(style);
    }, [map, style]);
}
/** Loads a base style for the map */
function useBaseStyle(baseLayer) {
    // Load base layer
    const [baseStyle, setBaseStyle] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        function loadStyle(styleUrl) {
            // Load style
            fetch(styleUrl)
                .then((response) => response.json())
                .then((styleData) => {
                // Set style and update layers
                setBaseStyle(styleData);
            });
        }
        if (baseLayer == "cartodb_positron") {
            loadStyle(`https://api.maptiler.com/maps/5f972163-6078-4341-982a-5c8bc4447e68/style.json?key=${mapTilerApiKey}`);
        }
        else if (baseLayer == "cartodb_dark_matter") {
            loadStyle(`https://api.maptiler.com/maps/1d3e957b-c546-4791-b9de-dce203538429/style.json?key=${mapTilerApiKey}`);
        }
        else if (baseLayer == "bing_road") {
            loadStyle(`https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerApiKey}`);
        }
        else if (baseLayer == "bing_aerial") {
            // Switched to Bing for superior aerial imagery
            loadBingBasemap("AerialWithLabels", 1).then(setBaseStyle);
            // loadStyle(`https://api.maptiler.com/maps/hybrid/style.json?key=${mapTilerApiKey}`)
        }
        else if (baseLayer == "blank") {
            setBaseStyle({
                version: 8,
                layers: [],
                sources: {}
            });
        }
    }, [baseLayer]);
    return baseStyle;
}
/** Combines a base style and a user style */
function mergeBaseAndUserStyle(baseStyle, userStyle, baseLayerOpacity) {
    // Merge until baseStyle and userStyle are present
    if (!baseStyle || !userStyle) {
        return null;
    }
    // Create background layer to simulate base layer opacity
    const baseLayerOpacityLayer = {
        id: "baseLayerOpacity",
        type: "background",
        paint: {
            "background-color": "white",
            "background-opacity": 1 - (baseLayerOpacity != null ? baseLayerOpacity : 1)
        }
    };
    // Create style
    let layers = baseStyle.layers || [];
    // Simulate base opacity
    if (baseLayerOpacity != null && baseLayerOpacity < 1) {
        layers = layers.concat([baseLayerOpacityLayer]);
    }
    layers = layers.concat(userStyle.layers || []);
    const style = {
        ...baseStyle,
        sources: {
            ...baseStyle.sources,
            ...userStyle.sources
        },
        glyphs: baseStyle.glyphs || `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${mapTilerApiKey}`,
        layers
    };
    return style;
}
function AttributionControl(props) {
    if (props.baseLayer == "blank") {
        return react_2.default.createElement("div", { className: "newmap-attribution-control" }, props.extraText ? " " + props.extraText : null);
    }
    if (props.baseLayer == "bing_aerial") {
        return (react_2.default.createElement("div", { className: "newmap-attribution-control" },
            "Copyright \u00A9 2022 Microsoft and its suppliers.",
            props.extraText ? " " + props.extraText : null));
    }
    return (react_2.default.createElement("div", { className: "newmap-attribution-control" },
        react_2.default.createElement("a", { href: "https://www.maptiler.com/copyright/", target: "_blank" }, "\u00A9 MapTiler"),
        " ",
        react_2.default.createElement("a", { href: "https://www.openstreetmap.org/about/", target: "_blank" }, "\u00A9 OpenStreetMap"),
        props.extraText ? " " + props.extraText : null));
}
function VectorMapLogo(props) {
    if (props.baseLayer == "blank") {
        return null;
    }
    if (props.baseLayer == "bing_aerial") {
        return react_2.default.createElement("img", { src: "https://dev.virtualearth.net/Branding/logo_powered_by.png", style: { position: "absolute", bottom: 38, left: 11, height: 22, zIndex: 1000, pointerEvents: "none" } });
    }
    return react_2.default.createElement("img", { src: require("./Maptiler-logo.png").default, style: { position: "absolute", bottom: 38, left: 11, height: 22, zIndex: 1000, pointerEvents: "none" } });
}
async function loadBingBasemap(basemapType, opacity) {
    // Load metadata
    const bingApiKey = "Ao26dWY2IC8PjorsJKFaoR85EPXCnCohrJdisCWXIULAXFo0JAXquGauppTMQbyU";
    const metadata = await fetch(`https://dev.virtualearth.net/REST/v1/Imagery/Metadata/${basemapType}?key=${bingApiKey}`).then((response) => response.json());
    const resource = metadata.resourceSets[0].resources[0];
    return {
        sources: {
            "bing_raster": {
                type: "raster",
                tiles: resource.imageUrlSubdomains.map((subdomain) => resource.imageUrl.replace("{subdomain}", subdomain).replace("{culture}", "").replace("http:", "https:")),
                tileSize: resource.imageHeight,
            }
        },
        layers: [
            {
                id: "bing_raster",
                type: "raster",
                source: "bing_raster",
                paint: {
                    "raster-opacity": opacity
                }
            }
        ],
        version: 8
    };
}
/** Persists map bounds to local storage */
function usePersistedMapBounds(map, localStorageKey) {
    const [bounds, setBounds] = (0, react_1.useState)(null);
    // Load bounds from local storage on initial render
    (0, react_1.useEffect)(() => {
        const persistedBounds = localStorage.getItem(localStorageKey);
        if (persistedBounds) {
            setBounds(JSON.parse(persistedBounds));
        }
    }, [localStorageKey]);
    // Apply loaded bounds to map when map and bounds are available
    (0, react_1.useEffect)(() => {
        if (map && bounds) {
            map.fitBounds([
                [bounds.w, bounds.s],
                [bounds.e, bounds.n]
            ], { duration: 2000 });
        }
    }, [map, bounds]);
    // Update bounds in local storage when map moves
    (0, react_1.useEffect)(() => {
        if (!map) {
            return;
        }
        function onMoveEnd() {
            const bounds = map.getBounds();
            const mapBounds = { n: bounds.getNorth(), e: bounds.getEast(), s: bounds.getSouth(), w: bounds.getWest() };
            localStorage.setItem(localStorageKey, JSON.stringify(mapBounds));
        }
        map.on("moveend", onMoveEnd);
        return () => {
            map.off("moveend", onMoveEnd);
        };
    }, [map]);
}
